import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency'
import EmailIcon from '@mui/icons-material/Email'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PhoneIcon from '@mui/icons-material/Phone'
import { Avatar, Button, Card, CardContent, Collapse, Divider, Grid2 as Grid, Link, Stack, Typography } from '@mui/material'
import * as React from 'react'
import { IoLogoWechat } from 'react-icons/io5'
import { useLocation, useNavigate } from 'react-router'

import { avatarUrl } from '../../utils/gravatar'
import { copyTextToClipboardSilent } from '../../utils/utility'
import { toTelHref } from '../../utils/utility'
import useBar from '../common/Bar'

/**
 * ContactCard - Displays user contact information in a card format
 *
 * @param {Object} user - User object containing contact information
 * @param {string} size - Size of the card (small, medium, large)
 * @param {boolean} emergencyContact - Whether to show emergency contact information
 * @param {boolean} disableNavigation - Whether to disable navigation on card click
 * @param {Object} sx - Additional styles to apply to the card
 */
export default function ContactCard({ user, size = 'medium', emergencyContact = true, disableNavigation = false, sx = {} }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [emergencyInfoExpanded, setEmergencyInfoExpanded] = React.useState(false)
  const bar = useBar()

  const avatarSizes = {
    small: 48,
    medium: 64,
    large: 120,
  }

  const resourceId = user?.resourceId || user?.value || user?.id
  const path = `/users/${resourceId}`

  const handleClick = () => {
    if (disableNavigation) return
    if (location.pathname === path) return
    navigate(path)
  }

  const toggleEmergencyInfo = (e) => {
    e.stopPropagation()
    setEmergencyInfoExpanded(!emergencyInfoExpanded)
  }

  if (!user || !user.name) {
    return null
  }

  const getNameInFamily = (user) => {
    if (user?.familyId && user?.familyMembers?.length > 0) {
      const familyMember = user?.familyMembers?.find((m) => m.familyId === user.familyId)
      return familyMember?.name || user?.name
    }
    return user?.name
  }

  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: disableNavigation || location.pathname === path ? 'default' : 'pointer',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: disableNavigation ? 'none' : 6,
          transform: disableNavigation || location.pathname === path ? 'none' : 'scale(1.01)',
          transition: 'all 0.2s ease-in-out',
        },
        ...sx,
      }}
    >
      <CardContent sx={{ p: size === 'large' ? 3 : 2 }}>
        <Stack spacing={size === 'large' ? 3 : 2}>
          {/* User basic info section */}
          <Grid container alignItems="center" spacing={2}>
            <Grid>
              <Avatar
                src={avatarUrl(user, size === 'large' ? 256 : 96)}
                sx={{
                  width: avatarSizes[size],
                  height: avatarSizes[size],
                  bgcolor: 'primary.main',
                  border: size === 'large' ? '2px solid' : 'none',
                  borderColor: 'background.paper',
                }}
              >
                {user.name[0] || '?'}
              </Avatar>
            </Grid>
            <Grid>
              <Typography
                variant={size === 'large' ? 'h4' : 'h6'}
                sx={{
                  color: 'text.primary',
                  fontWeight: size === 'large' ? 'bold' : 'medium',
                }}
              >
                {getNameInFamily(user)}
              </Typography>
            </Grid>
          </Grid>

          <Divider />

          {/* Contact information section */}
          <Stack spacing={1}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight="medium">
              Contact Info
            </Typography>

            {(user.phoneNumber || user.phone) && (
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon color="action" fontSize={size === 'small' ? 'small' : 'medium'} />
                <Link href={toTelHref(user.phoneNumber || user.phone)} underline="hover" color="inherit" variant="body1">
                  {user.phoneNumber || user.phone}
                </Link>
              </Stack>
            )}

            {user.email && (
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailIcon color="action" fontSize={size === 'small' ? 'small' : 'medium'} />
                <Link href={`mailto:${user.email}`} underline="hover" color="inherit" variant="body1">
                  {user.email}
                </Link>
              </Stack>
            )}

            {user.weChatId && (
              <Stack direction="row" spacing={1} alignItems="center">
                <IoLogoWechat style={{ fontSize: '1.5rem' }} />
                <Typography
                  variant="body1"
                  sx={{ cursor: 'pointer' }}
                  onClick={async (e) => {
                    e.stopPropagation()
                    const ok = await copyTextToClipboardSilent(user.weChatId)
                    if (ok) bar.info('WeChat ID copied to clipboard')
                    else bar.error('Failed to copy WeChat ID')
                  }}
                  title="Click to copy WeChat ID"
                >
                  {user.weChatId}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Emergency contact section */}
          {emergencyContact && user.emergencyContact && (
            <>
              <Divider />
              <Button
                onClick={toggleEmergencyInfo}
                endIcon={emergencyInfoExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                color="primary"
                size="small"
                variant="text"
                sx={{
                  justifyContent: 'flex-start',
                  p: 0,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <ContactEmergencyIcon color="error" fontSize={size === 'small' ? 'small' : 'medium'} sx={{ mr: 1 }} />
                <Typography variant="subtitle1" color="text.secondary" fontWeight="medium">
                  Emergency Contact
                </Typography>
              </Button>

              <Collapse in={emergencyInfoExpanded} timeout="auto" unmountOnExit>
                <Stack spacing={1} sx={{ pl: 2, mt: -1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1" fontWeight="medium">
                      {user.emergencyContact.name}
                    </Typography>
                  </Stack>

                  {user.emergencyContact.phone && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIcon color="action" fontSize="small" />
                      <Link href={toTelHref(user.emergencyContact.phone)} underline="hover" variant="body2">
                        {user.emergencyContact.phone}
                      </Link>
                    </Stack>
                  )}

                  {user.emergencyContact.relationship && (
                    <Typography variant="body2" color="text.secondary">
                      Relationship: {user.emergencyContact.relationship}
                    </Typography>
                  )}
                </Stack>
              </Collapse>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
